import HTTP from '@api';

import { generateXHRError, prettyJSONStr } from '@helpers';

export default {
  namespaced: true,
  state: {
    machines: [],
    machineDrawers: [],
    machineDrawersHistory: [],
    machineProxyTestResult: {},
    defaultMachineDescription: '',
  },
  mutations: {
    setMachines(state, payload) {
      state.machines = payload;
    },
    setDefaultMachineDescription(state, payload) {
      state.defaultMachineDescription = payload;
    },
    setMachineDrawers(state, payload) {
      state.machineDrawers = payload;
    },
    setMachineDrawersHistory(state, payload) {
      state.machineDrawersHistory = payload;
    },
    setMachineProxyTestResult(state, payload) {
      state.machineProxyTestResult = payload;
    },
  },
  actions: {
    async getMachines(
      { commit, rootState: { locations } },
      { suffix = locations.suffix, activeOnly = true } = {},
    ) {
      try {
        const { data } = await HTTP.get(`${suffix}/machines`, {
          params: { activeOnly },
        });
        commit('setMachines', data || []);
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(generateXHRError(e));
      }
    },

    async getDefaultMachineDescription(
      { commit, rootState: { locations } },
      { suffix = locations.suffix } = {},
    ) {
      try {
        const { data } = await HTTP.get(`${suffix}/machines/defaultdescription`);
        commit('setDefaultMachineDescription', prettyJSONStr(data));
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(generateXHRError(e));
      }
    },

    async getMachineDrawers(
      { commit, rootState: { locations } },
      { suffix = locations.suffix, machineName },
    ) {
      try {
        const { data } = await HTTP.get(`${suffix}/machine/${machineName}/drawer`);
        commit('setMachineDrawers', data || []);
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(generateXHRError(e));
      }
    },

    async getMachineDrawersHistory({ commit, rootState: { locations } }, {
      suffix = locations.suffix, machine, drawer, pageNum = 1, pageSize = 10,
    }) {
      try {
        const { data } = await HTTP.get(`${suffix}/machine/${machine}/drawer/${drawer}/history`, {
          params: { pageNum, pageSize },
        });
        commit('setMachineDrawersHistory', data || []);
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(generateXHRError(e));
      }
    },

    async createMachine({ rootState: { locations } }, {
      suffix = locations.suffix,
      name,
      stackerHeight,
      shortName = null,
      proxyUrl = null,
      defaultTrayName = null,
      category,
      description = null,
    }) {
      try {
        await HTTP.post(`${suffix}/machines`, {
          name, shortName, proxyUrl, category, defaultTrayName, stackerHeight, description,
        });
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(generateXHRError(e));
      }
    },

    async updateMachine({ rootState: { locations } }, {
      suffix = locations.suffix,
      currentName,
      name,
      stackerHeight,
      shortName = null,
      proxyUrl = null,
      defaultTrayName = null,
      description = null,
    }) {
      try {
        await HTTP.post(`${suffix}/machines/${currentName}`, {
          name, shortName, proxyUrl, defaultTrayName, stackerHeight, description,
        });
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(generateXHRError(e));
      }
    },

    async changeMachineState(
      { rootState: { locations } },
      { suffix = locations.suffix, enabled, name },
    ) {
      try {
        await HTTP.post(`${suffix}/machines/${name}/enabled`, { enabled });
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(generateXHRError(e));
      }
    },

    async runMachineProxyTest(
      { commit, rootState: { locations } },
      { suffix = locations.suffix, machineName } = {},
    ) {
      try {
        const { data } = await HTTP.get(`${suffix}/machines/${machineName}/proxytest`);
        commit('setMachineProxyTestResult', data || {});
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(generateXHRError(e));
      }
    },

    async createMachineDrawer({ rootState: { locations } }, {
      suffix = locations.suffix, machineName, drawer,
    }) {
      try {
        await HTTP.post(`${suffix}/machine/${machineName}/drawer`, drawer);
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(generateXHRError(e));
      }
    },

    async updateMachineDrawer({ rootState: { locations } }, {
      suffix = locations.suffix, machineName, currentDrawerName, drawer,
    }) {
      try {
        await HTTP.post(`${suffix}/machine/${machineName}/drawer/${currentDrawerName}`, drawer);
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(generateXHRError(e));
      }
    },
  },
  getters: {
    machineById: ({ machines }) => machines.reduce((acc, { id, name }) => {
      acc[id] = name;
      return acc;
    }, {}),
  },
};
